import React from 'react';
import { usePermissions } from '@askporter/auth-provider';
import { SearchTaskFilter } from '@askporter/client-grieg-lyric';
import { QuickFilterButton, QuickFilterButtonGroup } from '@askporter/component-library';
import { TaskFilterPermissions } from '@askporter/utils';
import { Actions } from '../../types';
import { todayAsIsoRange } from '../../utils';
import { DueTodayIcon, RespondTodayIcon, ResolveTodayIcon } from './QuickFilterIcons';

export interface QuickFiltersProps {
  t: (key: string, options?: Record<string, string | number>) => string;
  filters: SearchTaskFilter;
  filterPermissions: TaskFilterPermissions;
  actions: Actions;
}

/**
 * Renders the task Quick filter buttons
 * @param t - translation function
 * @param filters - filter state
 * @param actions - reducer action functions
 * @param filterPermissions - object describing the filter visibility
 */
export const QuickFilters: React.FC<React.PropsWithChildren<QuickFiltersProps>> = ({
  t,
  filters,
  actions,
  filterPermissions,
}: QuickFiltersProps) => {
  const showQuickFilterDueToday = usePermissions(filterPermissions.quickFilterDueToday);
  const showQuickFilterRespondToday = usePermissions(filterPermissions.quickFilterRespondToday);
  const showQuickFilterResolveToday = usePermissions(filterPermissions.quickFilterResolveToday);

  return (
    <QuickFilterButtonGroup t={t} groupLabel={t('ns.common:quick_filters_group:aria_label')} sx={{ mb: '24px' }}>
      {showQuickFilterDueToday && (
        <QuickFilterButton
          displayName={t('ns.task_filter:quick_filter:due_today')}
          filterKey="dueToday"
          isSelected={JSON.stringify(filters.resolutionDueDate) === JSON.stringify(todayAsIsoRange())}
          onClick={() => {
            const today = todayAsIsoRange();

            actions.setFilters({
              resolutionDueDate:
                JSON.stringify(filters.resolutionDueDate) === JSON.stringify(today)
                  ? { before: null, after: null }
                  : today,
            });
          }}
          icon={DueTodayIcon}
          size="large"
        />
      )}
      {showQuickFilterRespondToday && (
        <QuickFilterButton
          displayName={t('task_filter:quick_filter:respond_today')}
          filterKey="respondToday"
          isSelected={JSON.stringify(filters.responseDueDateTime) === JSON.stringify(todayAsIsoRange())}
          onClick={() => {
            const today = todayAsIsoRange();

            actions.setFilters({
              responseDueDateTime:
                JSON.stringify(filters.responseDueDateTime) === JSON.stringify(today)
                  ? { before: null, after: null }
                  : today,
            });
          }}
          icon={RespondTodayIcon}
          size="large"
        />
      )}
      {showQuickFilterResolveToday && (
        <QuickFilterButton
          displayName={t('task_filter:quick_filter:resolve_today')}
          filterKey="resolveToday"
          isSelected={JSON.stringify(filters.resolutionDueDateTime) === JSON.stringify(todayAsIsoRange())}
          onClick={() => {
            const today = todayAsIsoRange();

            actions.setFilters({
              resolutionDueDateTime:
                JSON.stringify(filters.resolutionDueDateTime) === JSON.stringify(today)
                  ? { before: null, after: null }
                  : today,
            });
          }}
          icon={ResolveTodayIcon}
          size="large"
        />
      )}
    </QuickFilterButtonGroup>
  );
};
